import { satisfies } from 'compare-versions';
import dayjs from 'dayjs';
import isFinite from 'lodash/isFinite';
import round from 'lodash/round';

import { K8sProvider } from '@cast/shared-types';
import {
  isClusterOperable,
  isEKSCluster,
} from '@cast/shared-cluster-context-data-access';
import {
  AgentStatus,
  Cluster,
  ClusterDisplayStatus,
  ClusterStatus,
} from '@cast/types';

export const STORAGE_SUPPORTED_PROVIDERS: K8sProvider[] = [
  K8sProvider.GKE,
  K8sProvider.EKS,
  K8sProvider.AKS,
];

// Update when AWS publishes new EKS extended-support calendar entries.
const K8S_VERSION_CALENDAR: Record<string, string> = {
  '2025-03-23': '1.30',
  '2024-11-26': '1.29',
  '2024-07-24': '1.28',
  '2024-06-11': '1.27',
  '2024-05-01': '1.26',
};

export const isDisconnectedForMonth = (cluster: Cluster): boolean =>
  cluster.agentStatus === AgentStatus.DISCONNECTED &&
  dayjs(cluster.agentSnapshotReceivedAt).isBefore(dayjs().subtract(30, 'day'));

export const isStorageReportsSupported = (cluster: Cluster): boolean =>
  STORAGE_SUPPORTED_PROVIDERS.includes(cluster.providerType);

export const isClusterWithOldK8s = (
  cluster: Cluster,
  isManaged: boolean
): boolean => {
  const today = dayjs().startOf('day');
  const calendarKey = Object.keys(K8S_VERSION_CALENDAR).find(
    (date) => today.isSame(date, 'day') || today.isAfter(date, 'day')
  );

  if (!calendarKey || !cluster.kubernetesVersion) {
    return false;
  }

  const supportedVersion = K8S_VERSION_CALENDAR[calendarKey];
  const isOperable = isClusterOperable(cluster, isManaged);
  const isOldK8s = satisfies(cluster.kubernetesVersion, `<${supportedVersion}`);

  return isEKSCluster(cluster) && isOperable && isOldK8s;
};

const dashIfNull = (num?: number): number | '--' =>
  isFinite(num) ? num! : '--';

export const formatDashboardMetricValue = (
  value: number | undefined,
  isOperational: boolean
): number | '--' => dashIfNull(isOperational ? round(value!, 2) : undefined);

export type ClusterDashboardAgentBannerState = {
  isDisconnected: boolean;
  clusterOnboardingPhase1: boolean;
  clusterOnboardingPhase2: boolean;
};

export const shouldShowAgentInstallBanner = (
  cluster: Cluster,
  {
    isDisconnected,
    clusterOnboardingPhase1,
    clusterOnboardingPhase2,
  }: ClusterDashboardAgentBannerState
): boolean =>
  cluster.displayStatus === ClusterDisplayStatus.DISCOVERED ||
  isDisconnected ||
  clusterOnboardingPhase1 ||
  clusterOnboardingPhase2;

export const isClusterDiscovered = (cluster: Cluster): boolean =>
  cluster.displayStatus === ClusterDisplayStatus.DISCOVERED;

export const shouldShowReconcileInfo = (
  cluster: Cluster,
  isManaged: boolean
): boolean => isManaged && cluster.providerType !== K8sProvider.ANYWHERE;

export const isClusterConnecting = (cluster: Cluster): boolean =>
  cluster.status === ClusterStatus.CONNECTING;
